import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/domain/auth/service/auth.service';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { PhoneService } from 'src/app/domain/login/service/phone.service';
import { VerificationMethod } from '../dto/login.dto';
import { PageType } from '../dto/types';
import { SpinnerService } from 'src/app/core/service/spinner.service';
import { RegExpService } from "../../../../util/common/reg-exp.service";

type VerifyPageType = 'verify' | 'changeMethod';

@Component({
  selector: 'app-verify-sms-form',
  templateUrl: './verify-sms-form.component.html',
  styleUrls: [
    '../entrance-group.scss',
    './verify-sms-form.component.scss',
  ],
})
export class VerifySmsFormComponent implements OnDestroy {
  readonly regExpService = RegExpService;
  readonly supportEmail = 'helpdesk@orcainc.com';

  @Output() newPageEvent = new EventEmitter<PageType>();
  @Input() previousPage: PageType;

  code = '';

  countdownInterval: NodeJS.Timeout | null = null;
  countdownSeconds = 0;
  counter = 0;
  attemptsCounter = 4;
  verificationMethodsMap = { sms: 'SMS', whatsapp: 'WhatsApp' };
  currentVerificationMethod = localStorage.getItem('verificationMethod') as VerificationMethod;
  verificationMethods = [{ id: 'sms', name: 'SMS' }, { id: 'whatsapp', name: 'WhatsApp' }];
  maskedPhoneNumber = localStorage.getItem('maskedPhoneNumber');
  maskLen = this.maskedPhoneNumber.length;
  lastNumbs = this.maskedPhoneNumber.slice(this.maskLen - 4);
  beforeLastFour = this.maskedPhoneNumber.slice(0, this.maskLen - 4);

  @ViewChild('spinnerDiv') spinnerDiv: ElementRef;

  page: VerifyPageType = 'verify';

  isSpinnerActive = false;
  isFormDisabled = false;

  constructor(
    private routerService: Router,
    private phoneService: PhoneService,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private spinner: SpinnerService,
  ) {
    this.startTimerForNextSms();
    localStorage.removeItem('maskedPhoneNumber');
    localStorage.removeItem('verificationMethod');
  }

  ngOnDestroy(): void {
    clearInterval(this.countdownInterval);
  }

  changePage(page: PageType): void {
    this.newPageEvent.emit(page);
  }

  changeVerifyPage(page: VerifyPageType): void {
    this.page = page;
  }

  // Prevent default behavior when Enter is pressed
  preventEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default action
    }
  }

  async submitIfSixDigits(): Promise<void> {
    const { code } = this;
    if (code.length === 6) {
      await this.withSpinner(this.onSubmit());
    }
  }

  async onSubmit(): Promise<void> {
    this.isFormDisabled = true;
    const { code } = this;
    if (code.length === 0) {
      this.notificationsService.error(NOTIFICATIONS.VERIFICATION_CODE_IS_TOO_SHORT_ERROR);
      this.isFormDisabled = false;
      return;
    }
    try {
      await this.phoneService.verifySmsCode(code);
      if (localStorage.getItem('termsAndConditions')) {
        this.changePage('termsAndConditions');
        return;
      }
      this.authService.setCurrentUserType('viewer');
      this.routerService.navigateByUrl('/entrance');
    } catch (err) {
      this.isFormDisabled = false;
      const x = { title: 'Invalid code', message: 'Please check your code and try again. ' };
      x.message += (this.attemptsCounter) + ' attempts left';
      this.counter++;
      if (this.attemptsCounter === 0) {
        this.changePage('login');
        this.notificationsService.error(NOTIFICATIONS.MAX_ATTEMPTS_REACHED);
      } else {
        this.attemptsCounter--;
        this.notificationsService.error(x);
      }
    }
  }

  async changeVerifyMethod(method: VerificationMethod): Promise<void> {
    this.currentVerificationMethod = method;
    await this.resendCode();
    this.changeVerifyPage('verify');
  }

  async withSpinner(promise: Promise<void>): Promise<void> {
    this.isSpinnerActive = true;
    this.spinner.startLoadingForComponent(this.spinnerDiv);
    await promise;
    this.spinner.finishLoadingForComponent(this.spinnerDiv);
    this.isSpinnerActive = false;

  }

  private async resendCode(): Promise<void> {
    this.code = '';
    if (this.countdownInterval !== null) return;
    this.startTimerForNextSms();
    try {
      await this.phoneService.resendSmsCode(this.currentVerificationMethod);
      this.notificationsService.success(NOTIFICATIONS.RESEND_SMS_CODE_SUCCESS);
    } catch (err) {
      this.notificationsService.error(NOTIFICATIONS.NETWORK_ERROR);
    }
  }

  private startTimerForNextSms(): void {
    const sixtySeconds = 60000 / 2; // in milliseconds
    const countdownDate = new Date().getTime() + sixtySeconds;
    this.countdownSeconds = 60 / 2;

    this.countdownInterval = setInterval(() => {
      const now = new Date().getTime();
      const distance = countdownDate - now;
      this.countdownSeconds = Math.floor((distance % (1000 * 30)) / 1000); // get seconds;

      if (distance < 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        this.countdownSeconds = 0;
      }
    }, 1000);
  }

}
